import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RoutesRecognized,
} from '@angular/router';
import { Subscription } from 'rxjs';

/** Snapshot the departing route so its slide can finish after RouterOutlet destroys it. */
export class LoginRouteTransition {
  private snapshot?: HTMLElement;
  private animations: Animation[] = [];
  private entering?: Element;
  private subscription: Subscription;

  constructor(
    private readonly router: Router,
    private readonly container: HTMLElement,
  ) {
    this.subscription = router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.clear();
      } else if (event instanceof RoutesRecognized) {
        // Recognition happens before outlet activation destroys the departing route.
        // Ordinary navigation must not clone potentially large galleries and lists.
        if (
          event.state.root.firstChild?.data['animation'] !== 'LoginAnimation' ||
          this.prefersReducedMotion()
        )
          return;
        this.snapshot = this.container.cloneNode(true) as HTMLElement;
        this.snapshot.classList.add('login-route-snapshot');
        this.snapshot.inert = true;
        this.snapshot.setAttribute('aria-hidden', 'true');
      } else if (event instanceof NavigationEnd) {
        const outlet = this.container.querySelector('router-outlet');
        const component = outlet?.nextElementSibling;
        const route = this.router.routerState.root.firstChild;
        if (
          route?.snapshot.data['animation'] !== 'LoginAnimation' ||
          !component ||
          this.prefersReducedMotion()
        ) {
          this.clear();
          return;
        }
        this.entering = component;
        component.classList.add('login-route-entering');
        if (this.snapshot) {
          this.container.appendChild(this.snapshot);
          this.animations.push(
            this.snapshot.animate(
              [
                { transform: 'translateX(0)' },
                { transform: 'translateX(100%)' },
              ],
              { duration: 200, easing: 'ease-out', fill: 'both' },
            ),
          );
        }
        this.animations.push(
          component.animate(
            [
              { transform: 'translateX(-100%)' },
              { transform: 'translateX(0)' },
            ],
            { duration: 300, easing: 'ease-out', fill: 'both' },
          ),
        );
        const animations = this.animations;
        Promise.allSettled(
          animations.map((animation) => animation.finished),
        ).then(() => {
          if (this.animations === animations) this.clear();
        });
      } else if (
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.clear();
      }
    });
  }

  private prefersReducedMotion(): boolean {
    return (
      this.container.ownerDocument.defaultView?.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches ?? false
    );
  }

  private clear(): void {
    for (const animation of this.animations) animation.cancel();
    this.animations = [];
    this.snapshot?.remove();
    this.snapshot = undefined;
    this.entering?.classList.remove('login-route-entering');
    this.entering = undefined;
  }

  destroy(): void {
    this.subscription.unsubscribe();
    this.clear();
  }
}
